/**
 * M7 S7: stated claims (spec §2.3, §3.1). Weight unlocks the attempt; the player's claim advances the tier.
 *
 * - `claimUnlocked`: the next tier's attempt opens on read evidence (tier 2: proof weight in enough threads; tier 3:
 *   `watch` weight; tier 4: the report core or its lab notes; tier 5: the 50-A file or Hale's copy terms).
 * - `wordPool`: words from read logs (only the template's slot types) plus the template's decoys. A slot whose
 *   answer is in the pool always has another same-type word beside it (typed slots never give an answer away); the
 *   padding never adds an answer, so an answer only ever comes from reading.
 * - `submitClaim`: correct → `setStoryTier(n)` (the Career's `storyTier` too; the open questions close because they
 *   derive from the tier); wrong → `claims.json feedback.wrong` and nothing else (never a count or a per-word hint).
 *   A retry needs a newly read log or the next faction day; at most `attemptsPerDay` per faction day per tier.
 * - Kell's safety net (credits only; the Mutual archivist when Holdfast won't talk) after every proof of the
 *   template's thread is read, or after the failed attempts plus the online days; sets `claim_assisted_<n>`.
 * - `claimContact`: the framed recipient, or who takes the claim on comms when its dock is refused.
 *
 * `storyTier` 0 (or absent) reads as tier 1. Pure, Career-in, Career-out; the data is passed in.
 */
import { withWallet } from '../career/ops'
import type { Career } from '../career/types'
import { storyData } from './data'
import { itemWithPlayer } from './items'
import type { ClaimsConfig, EvidenceLog } from './schemas'
import { hasFlag, onlineDaysOf, setFlag, storyOf, withStory } from './state'
import type { Speaker, StoryClaim, StoryEvent, StoryFlag, StoryResult, Thread } from './types'

export type ClaimTemplate = ClaimsConfig['templates'][number]

/** What the claim logic reads: `claims.json` and the evidence by id. */
export interface ClaimData {
  claims: ClaimsConfig
  logsById: ReadonlyMap<string, EvidenceLog>
}

export function claimData(): ClaimData {
  const d = storyData()
  return { claims: d.claims, logsById: d.logsById }
}

export const MIN_CLAIM_TIER = 2
export const MAX_CLAIM_TIER = 5

/** The story tier as the claims read it: 0 (a new career) and absent read as tier 1. */
export function currentTier(career: Career): number {
  return Math.max(1, career.storyTier ?? 0)
}

/** The tier the next claim would set, or null once tier 5 is claimed. */
export function nextClaimTier(career: Career): number | null {
  const next = currentTier(career) + 1
  return next <= MAX_CLAIM_TIER ? next : null
}

export function templateFor(tier: number, data: ClaimData = claimData()): ClaimTemplate | undefined {
  return data.claims.templates.find((t) => t.tier === tier)
}

export function claimRecord(career: Career, tier: number): StoryClaim | undefined {
  return storyOf(career).claims.find((c) => c.tier === tier)
}

function readLogsOf(career: Career, data: ClaimData): EvidenceLog[] {
  const out: EvidenceLog[] = []
  for (const id of storyOf(career).readLogs) {
    const log = data.logsById.get(id)
    if (log !== undefined) out.push(log)
  }
  return out
}

/** Σ weight of read logs per thread (`proofOnly`: proofs only, the tier-2 rule). */
function readWeight(career: Career, data: ClaimData, proofOnly: boolean): Map<Thread, number> {
  const w = new Map<Thread, number>()
  for (const log of readLogsOf(career, data)) {
    if (proofOnly && log.kind !== 'proof') continue
    w.set(log.thread, (w.get(log.thread) ?? 0) + log.weight)
  }
  return w
}

/** The player has the item (held, a copy, or Hale holds the original) or has read the item's contents. */
function hasItemEvidence(career: Career, items: readonly string[], data: ClaimData): boolean {
  const s = storyOf(career)
  if (s.items.some((i) => items.includes(i.id) && itemWithPlayer(i))) return true
  return readLogsOf(career, data).some((l) => l.item !== undefined && items.includes(l.item))
}

/** Whether the evidence opens an attempt at `tier` (the next tier only; a claimed tier is closed). */
export function claimUnlocked(career: Career, tier: number, data: ClaimData = claimData()): boolean {
  if (tier !== nextClaimTier(career)) return false
  const u = data.claims.unlock
  switch (tier) {
    case 2: {
      let threads = 0
      for (const w of readWeight(career, data, true).values()) if (w >= u.tier2.proofWeight) threads++
      return threads >= u.tier2.threads
    }
    case 3:
      return (readWeight(career, data, false).get('watch') ?? 0) >= u.tier3.watchWeight
    case 4:
      return hasItemEvidence(career, u.tier4.items, data)
    case 5:
      return hasItemEvidence(career, u.tier5.items, data) || u.tier5.flags.some((f) => hasFlag(career, f))
    default:
      return false
  }
}

function typeOf(word: string, data: ClaimData): string | undefined {
  return data.claims.words[word]?.type
}

/**
 * The words the player may place for `tier`'s claim, sorted by id (reading order would leak the answers): read
 * words of the template's slot types, the decoys, and for each slot type whose answer is in the pool a same-type
 * non-answer word when reading gave none. Empty when there is no template for `tier`.
 */
export function wordPool(career: Career, tier: number, data: ClaimData = claimData()): string[] {
  const t = templateFor(tier, data)
  if (t === undefined) return []
  const types = new Set(t.slots.map((s) => s.type))
  const answers = new Set(t.slots.map((s) => s.answer))
  const pool = new Set<string>()
  for (const log of readLogsOf(career, data)) {
    for (const w of log.words) {
      const type = typeOf(w, data)
      if (type !== undefined && types.has(type)) pool.add(w)
    }
  }
  for (const d of t.decoys) pool.add(d.word)
  for (const type of types) {
    const ofType = [...pool].filter((w) => typeOf(w, data) === type)
    if (ofType.length >= 2 || !ofType.some((w) => answers.has(w))) continue
    const pad = Object.keys(data.claims.words)
      .filter((w) => typeOf(w, data) === type && !answers.has(w) && !pool.has(w))
      .sort()
    for (const w of pad.slice(0, 2 - ofType.length)) pool.add(w)
  }
  return [...pool].sort()
}

/** The display text of a word id (`ops_r7` → "OPS-R/7"); the id itself when the dictionary lacks it. */
export function wordText(word: string, data: ClaimData = claimData()): string {
  return data.claims.words[word]?.text ?? word
}

/** The template sentence with `words` (or blanks) in its slots, as display text. */
export function claimSentence(tier: number, words: readonly (string | null)[], data: ClaimData = claimData()): string {
  const t = templateFor(tier, data)
  if (t === undefined) return ''
  return t.sentence.replace(/\{(\d+)\}/g, (_m, i: string) => {
    const w = words[Number(i)]
    return w == null ? '____' : wordText(w, data)
  })
}

// ---------------------------------------------------------------------------------------------
// Attempts and the retry rule
// ---------------------------------------------------------------------------------------------

export type ClaimRefusal =
  /** The tier is already claimed, or isn't the next one. */
  | 'notNext'
  /** The evidence doesn't open the attempt yet. */
  | 'locked'
  /** `attemptsPerDay` attempts already made this faction day. */
  | 'dayLimit'
  /** Same faction day and no log read since the last attempt (re-reading never counts). */
  | 'needNewLog'
  /** Wrong slot count, or a word not in the pool (no attempt is used). */
  | 'badWords'

/** Why an attempt at `tier` on faction day `day` is refused, or null when it may be made. */
export function attemptRefusal(career: Career, tier: number, day: number, data: ClaimData = claimData()): ClaimRefusal | null {
  if (tier !== nextClaimTier(career)) return 'notNext'
  if (!claimUnlocked(career, tier, data)) return 'locked'
  const rec = claimRecord(career, tier)
  if (rec === undefined || rec.attempts === 0 || day !== rec.lastAttemptDay) return null
  if (rec.dayAttempts >= data.claims.attemptsPerDay) return 'dayLimit'
  if (storyOf(career).readLogs.length <= rec.readMark) return 'needNewLog'
  return null
}

/** The record after one attempt on `day`. */
function recordAttempt(career: Career, tier: number, day: number, failed: boolean, data: ClaimData): Career {
  const prev = claimRecord(career, tier)
  const sameDay = prev !== undefined && prev.attempts > 0 && prev.lastAttemptDay === day
  const attempts = (prev?.attempts ?? 0) + 1
  const reachedNet = failed && attempts >= data.claims.safetyNet.failedAttempts
  const failOnlineDay = prev?.failOnlineDay ?? (reachedNet ? onlineDaysOf(career) : undefined)
  const next: StoryClaim = {
    tier,
    attempts,
    lastAttemptDay: day,
    dayAttempts: sameDay ? prev.dayAttempts + 1 : 1,
    readMark: storyOf(career).readLogs.length,
    assisted: prev?.assisted ?? false,
    ...(failOnlineDay !== undefined ? { failOnlineDay } : {}),
  }
  return withClaim(career, next)
}

function withClaim(career: Career, rec: StoryClaim): Career {
  return withStory(career, (s) => ({
    ...s,
    claims: s.claims.some((c) => c.tier === rec.tier) ? s.claims.map((c) => (c.tier === rec.tier ? rec : c)) : [...s.claims, rec],
  }))
}

/** Sets the tier on the Career and asks the worker for it. */
function advance(career: Career, tier: number): StoryResult<Career> {
  return {
    career: { ...career, storyTier: tier },
    commands: [{ type: 'setStoryTier', tier }],
    effects: [{ type: 'autosave', reason: `claim${tier}` }],
  }
}

export interface ClaimInput {
  tier: number
  /** One word id per slot, in slot order. */
  words: readonly string[]
  /** The worker's faction day (`StoryWorldView.day`). */
  day: number
}

export type ClaimOutcome =
  | { ok: false; reason: ClaimRefusal }
  | {
      ok: true
      correct: boolean
      /** `claims.json feedback.wrong` on a wrong claim; null on a correct one (the recipient answers on comms). */
      feedback: string | null
      /** For the mission runtime (spec §2.3 "Then"). */
      event: StoryEvent
      result: StoryResult<Career>
    }

/** States a claim. A refusal changes nothing; an attempt is recorded either way. */
export function submitClaim(career: Career, input: ClaimInput, data: ClaimData = claimData()): ClaimOutcome {
  const { tier, words, day } = input
  const refusal = attemptRefusal(career, tier, day, data)
  if (refusal !== null) return { ok: false, reason: refusal }
  const t = templateFor(tier, data)
  if (t === undefined || words.length !== t.slots.length) return { ok: false, reason: 'badWords' }
  const pool = new Set(wordPool(career, tier, data))
  if (!words.every((w) => pool.has(w))) return { ok: false, reason: 'badWords' }
  const correct = t.slots.every((s, i) => words[i] === s.answer)
  const recorded = recordAttempt(career, tier, day, !correct, data)
  const event: StoryEvent = { type: 'claimMade', tier, correct }
  if (correct) return { ok: true, correct, feedback: null, event, result: advance(recorded, tier) }
  return { ok: true, correct, feedback: data.claims.feedback.wrong, event, result: { career: recorded, commands: [], effects: [] } }
}

// ---------------------------------------------------------------------------------------------
// Kell's safety net
// ---------------------------------------------------------------------------------------------

export type NetProvider = Extract<Speaker, 'kell' | 'mutualArchivist'>

export interface SafetyNetOffer {
  provider: NetProvider
  /** Credits only (rev 2). */
  fee: number
}

/** The thread's budget proofs that can drop before `tier` is claimed (no mission items, templates or boot logs). */
export function netProofs(tier: number, data: ClaimData = claimData()): EvidenceLog[] {
  const t = templateFor(tier, data)
  if (t === undefined) return []
  const out: EvidenceLog[] = []
  for (const log of data.logsById.values()) {
    if (log.thread !== t.thread || log.kind !== 'proof' || log.fromTier >= tier) continue
    if (log.item !== undefined || log.repeatable || log.template || log.outsideBudget) continue
    out.push(log)
  }
  return out
}

/** Kell's offer to "take a look" at `tier`'s claim, or null when it isn't open. */
export function safetyNetOffer(career: Career, tier: number, data: ClaimData = claimData()): SafetyNetOffer | null {
  if (!claimUnlocked(career, tier, data)) return null
  const net = data.claims.safetyNet
  const read = new Set(storyOf(career).readLogs)
  const proofs = netProofs(tier, data)
  const allRead = proofs.length > 0 && proofs.every((l) => read.has(l.id))
  const rec = claimRecord(career, tier)
  const waited =
    rec !== undefined &&
    rec.attempts >= net.failedAttempts &&
    rec.failOnlineDay !== undefined &&
    onlineDaysOf(career) - rec.failOnlineDay >= net.onlineDaysAfter
  if (!allRead && !waited) return null
  const kellRefuses = career.standings.holdfast <= net.holdfastRefusesAt
  return kellRefuses ? { provider: 'mutualArchivist', fee: net.archivistFee } : { provider: 'kell', fee: net.kellFee }
}

export type SafetyNetOutcome =
  | { ok: false; reason: 'noOffer' | 'funds' }
  | { ok: true; offer: SafetyNetOffer; event: StoryEvent; result: StoryResult<Career> }

const ASSIST_FLAGS: Readonly<Record<number, StoryFlag>> = {
  2: 'claim_assisted_2',
  3: 'claim_assisted_3',
  4: 'claim_assisted_4',
  5: 'claim_assisted_5',
}

/** Pays the fee (credits only) and advances the tier; sets `claim_assisted_<tier>` for the roll call and epilogue. */
export function acceptSafetyNet(career: Career, tier: number, data: ClaimData = claimData()): SafetyNetOutcome {
  const offer = safetyNetOffer(career, tier, data)
  const flag = ASSIST_FLAGS[tier]
  if (offer === null || flag === undefined) return { ok: false, reason: 'noOffer' }
  if (career.wallet < offer.fee) return { ok: false, reason: 'funds' }
  const prev = claimRecord(career, tier)
  const rec: StoryClaim = prev !== undefined
    ? { ...prev, assisted: true }
    : { tier, attempts: 0, lastAttemptDay: 0, dayAttempts: 0, readMark: storyOf(career).readLogs.length, assisted: true }
  const paid = setFlag(withClaim(withWallet(career, -offer.fee), rec), flag)
  return { ok: true, offer, event: { type: 'claimMade', tier, correct: true }, result: advance(paid, tier) }
}

// ---------------------------------------------------------------------------------------------
// Recipients and open questions
// ---------------------------------------------------------------------------------------------

export interface ClaimContact {
  speaker: Speaker
  /** True when the claim goes by comms instead of the recipient's dock (spec §3.1). */
  fallback: boolean
}

/** Who takes `tier`'s claim: the framed recipient, or (`refused`: its dock is refused) the comms fallback. */
export function claimContact(career: Career, tier: number, refused: boolean, data: ClaimData = claimData()): ClaimContact | null {
  const t = templateFor(tier, data)
  if (t === undefined) return null
  if (!refused) return { speaker: t.recipient, fallback: false }
  const alt = t.fallback.find((f) => f.flag === undefined || hasFlag(career, f.flag))
  return { speaker: alt?.speaker ?? t.recipient, fallback: true }
}

export interface OpenQuestion {
  id: string
  text: string
  tier: number
}

/** The Logs screen's open questions: those of unclaimed tiers that a read log has raised. A claim closes its own. */
export function openQuestions(career: Career, data: ClaimData = claimData()): OpenQuestion[] {
  const tier = currentTier(career)
  const read = new Set(storyOf(career).readLogs)
  const out: OpenQuestion[] = []
  for (const t of data.claims.templates) {
    if (t.tier <= tier) continue
    for (const q of t.questions) if (q.after.some((id) => read.has(id))) out.push({ id: q.id, text: q.text, tier: t.tier })
  }
  return out
}
